import { Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system/legacy';

import {
  isUnexpectedFailureHandled,
  markUnexpectedFailureHandled,
  presentUnexpectedFailure,
} from '../../sentry/presentUnexpectedFailure';

export type PickedCsvFile = {
  name: string;
  text: string;
};

const CSV_PICKER_TYPES = [
  'text/csv',
  'text/comma-separated-values',
  'text/plain',
  'application/vnd.ms-excel',
  '*/*',
] as const;

async function readPickedAssetText(uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error('No se pudo leer el archivo seleccionado.');
    }
    return response.text();
  }
  return FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.UTF8 });
}

/** Web: input nativo (gesto del usuario + File.text, sin blob:/fetch). */
function pickCsvFileWebInput(): Promise<PickedCsvFile | null> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.txt,text/csv,text/plain';
    Object.assign(input.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '1px',
      height: '1px',
      opacity: '0',
      overflow: 'hidden',
    });

    let settled = false;
    const finish = (value: PickedCsvFile | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        finish(null);
        return;
      }
      void file
        .text()
        .then((text) => finish({ name: file.name, text }))
        .catch((err) => reject(err instanceof Error ? err : new Error(String(err))));
    };

    input.oncancel = () => finish(null);

    document.body.appendChild(input);
    try {
      if (typeof input.showPicker === 'function') {
        input.showPicker();
      } else {
        input.click();
      }
    } catch {
      try {
        input.click();
      } catch (clickErr) {
        input.remove();
        reject(clickErr instanceof Error ? clickErr : new Error(String(clickErr)));
      }
    }
  });
}

async function pickCsvFileWithDocumentPicker(): Promise<PickedCsvFile | null> {
  const result = await DocumentPicker.getDocumentAsync({
    type: [...CSV_PICKER_TYPES],
    copyToCacheDirectory: Platform.OS !== 'web',
    multiple: false,
  });
  if (result.canceled || !result.assets?.[0]?.uri) {
    return null;
  }
  const asset = result.assets[0];
  const text = await readPickedAssetText(asset.uri);
  return { name: asset.name ?? 'import.csv', text };
}

function presentPickFailureAndThrow(e: unknown): never {
  const userMessage = e instanceof Error ? e.message : 'No se pudo leer el archivo.';
  presentUnexpectedFailure({
    module: 'csv',
    op: Platform.OS === 'web' ? 'pick_web' : 'pick_native',
    error: e,
    title: 'Importar',
    userMessage,
  });
  const err = e instanceof Error ? e : new Error(userMessage);
  throw markUnexpectedFailureHandled(err);
}

export async function pickCsvFile(): Promise<PickedCsvFile | null> {
  // Web: no DocumentPicker — createObjectURL + fetch(blob:) choca con CSP connect-src
  // y el fallback pierde el user activation ("File chooser… user activation").
  try {
    if (Platform.OS === 'web') {
      return await pickCsvFileWebInput();
    }
    return await pickCsvFileWithDocumentPicker();
  } catch (e) {
    if (isUnexpectedFailureHandled(e)) throw e;
    presentPickFailureAndThrow(e);
  }
}
